import React, { useMemo } from 'react';

import {
  type QueryParams,
  addIdeaToQuery,
  invalidateIdeaQueries,
  loadMore,
  loadQuery,
  reloadIdeaQuery,
} from 'common/actions/ideaQueries';
import { removeIdeas as removeIdeasAction, upsertIdeas } from 'common/actions/ideas';
import connect from 'common/core/connect';

export { getQueryKey } from 'common/actions/ideaQueries';

import type { Idea } from 'common/api/endpoints/idea';
import type { IdeaQueryState } from 'common/reducers/ideaQueries';
import type { QueryState } from 'common/reducers/queryReducerFactory';
import type { Dispatch, GetState } from 'redux-connect';

export type { QueryParams } from 'common/actions/ideaQueries';
export type { IdeaIDQueriesState, IdeaQueryState } from 'common/reducers/ideaQueries';

export type ResolvedIdeaQueryState = QueryState<Idea, QueryParams>;

type IdeaQueriesContextValue = {
  loadMore: (query: IdeaQueryState) => Promise<void>;
  loadQuery: (queryParams: QueryParams) => Promise<void>;
  invalidateIdeaQueries: () => Promise<void>;
  reloadQuery: (queryParams: QueryParams) => Promise<void>;
  upsertIdeas: (ideas: Idea[]) => Promise<void>;
  removeIdeas: (ideas: Idea[]) => Promise<void>;
  addIdeaToQuery: (queryParams: QueryParams, item: Pick<Idea, '_id'>) => Promise<void>;
};

export const IdeaQueriesContext = React.createContext<IdeaQueriesContextValue>({
  loadMore: (_query: IdeaQueryState) => Promise.resolve(),
  loadQuery: (_queryParams: QueryParams) => Promise.resolve(),
  invalidateIdeaQueries: () => Promise.resolve(),
  reloadQuery: (_iqueryParams: QueryParams) => Promise.resolve(),
  upsertIdeas: (_ideas: Idea[]) => Promise.resolve(),
  removeIdeas: (_ideas: Idea[]) => Promise.resolve(),
  addIdeaToQuery: (_queryParams: QueryParams, _item: Pick<Idea, '_id'>) => Promise.resolve(),
});

type OwnProps = {
  children: React.ReactNode;
};

type Props = IdeaQueriesContextValue & OwnProps;

function IdeaListContainer({
  children,
  loadMore,
  loadQuery,
  invalidateIdeaQueries,
  reloadQuery,
  upsertIdeas,
  removeIdeas,
  addIdeaToQuery,
}: Props) {
  const contextValue = useMemo(
    () => ({
      loadMore,
      loadQuery,
      invalidateIdeaQueries,
      reloadQuery,
      upsertIdeas,
      removeIdeas,
      addIdeaToQuery,
    }),
    [
      loadMore,
      loadQuery,
      invalidateIdeaQueries,
      reloadQuery,
      upsertIdeas,
      removeIdeas,
      addIdeaToQuery,
    ],
  );
  return <IdeaQueriesContext.Provider value={contextValue}>{children}</IdeaQueriesContext.Provider>;
}

export default connect(
  (state: any) => ({
    ideasState: state.ideas,
  }),
  {
    loadMore,
    loadQuery,
    invalidateIdeaQueries,
    reloadQuery: reloadIdeaQuery,
    upsertIdeas,
    removeIdeas: (ideas: Idea[]) => (dispatch: Dispatch, getState: GetState) => {
      return dispatch(removeIdeasAction(ideas, getState().ideas));
    },
    addIdeaToQuery,
  },
)(IdeaListContainer) as unknown as React.FC<OwnProps>;
